import { useEffect, useMemo, useRef, useState } from "react";
import { buildPreview, PREVIEW_SANDBOX, type BridgeMessage } from "@buildlab/preview";
import type { FileMap } from "@buildlab/shared";
import { answerClassDataRequest, answerDataRequest } from "./api";
import { BigExit, FitControls, useFit } from "./FitFrame";
import "./ClassShare.css";

// Play a classmate's shared project full-screen (decision 62). It runs the frozen snapshot in the
// same sandbox as the preview; links between a website's pages work. saveData lasts only while the
// player is open (it never writes into the classmate's project). The teacher uses this too, to look
// at a share that's waiting for them.

export function GalleryPlayer({
  files,
  title,
  byline,
  galleryId,
  onClose,
  onRemix,
  remixing,
  children,
}: {
  files: FileMap;
  title: string;
  byline: string;
  /** The live entry being played, so shareData from the game can be traced back to it. */
  galleryId?: string;
  onClose: () => void;
  onRemix?: () => void;
  remixing?: boolean;
  /** Extra buttons for the header (the teacher's choice on a held share). */
  children?: React.ReactNode;
}) {
  const frame = useRef<HTMLIFrameElement>(null);
  const saved = useRef<Record<string, unknown>>({});
  const [run, setRun] = useState(0);
  const pages = Object.keys(files)
    .filter((f) => /\.html?$/i.test(f))
    .sort((a, b) => (a === "index.html" ? -1 : b === "index.html" ? 1 : a.localeCompare(b)));
  const [page, setPage] = useState("index.html");
  const shown = files[page] !== undefined ? page : (pages[0] ?? "index.html");
  const html = useMemo(() => buildPreview(files, { page: shown, savedData: saved.current }).html, [files, shown, run]);
  const fit = useFit(frame, `${shown}-${run}`); // the whole game shows, however big the window (Review 17, C61)

  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      const w = frame.current?.contentWindow;
      if (!w || e.source !== w) return;
      const m = e.data as BridgeMessage;
      if (!m || m.__buildlab !== 1) return;
      if (m.kind === "navigate") setPage(m.page);
      else if (m.kind === "getData") answerDataRequest(w, m);
      else if (m.kind === "classData") answerClassDataRequest(w, m, galleryId ? { galleryId } : {});
      else if (m.kind === "saveData") saved.current = { ...saved.current, [m.key]: m.value };
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !document.fullscreenElement && !document.querySelector(".fit-big")) onClose(); // Esc in Play big only leaves Play big
    };
    window.addEventListener("message", onMessage);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("message", onMessage);
      window.removeEventListener("keydown", onKey);
    };
  }, [galleryId, onClose]);

  return (
    <div className="cs-player" role="dialog" aria-modal="true" aria-label={`Playing ${title}`}>
      <header className="cs-player-head">
        <div className="cs-player-title">
          <span className="eyebrow gold">Class gallery</span>
          <h2>{title}</h2>
          <span className="small">by {byline}</span>
        </div>
        {pages.length > 1 && (
          <label className="cs-page">
            <span className="sr-only">Page</span>
            <select value={shown} onChange={(e) => setPage(e.target.value)} aria-label="Page">
              {pages.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </label>
        )}
        <div className="row cs-player-actions">
          <FitControls fit={fit} />
          <button type="button" className="pill-dark" onClick={() => setRun((r) => r + 1)}>
            Restart
          </button>
          {onRemix && (
            <button type="button" className="primary small" disabled={remixing} onClick={onRemix}>
              {remixing ? "Making your copy…" : "Remix"}
            </button>
          )}
          {children}
          <button type="button" className="pill-dark" onClick={onClose}>
            Close
          </button>
        </div>
      </header>
      <div ref={fit.stageRef} className={`cs-player-stage ${fit.stageClass}`}>
        <iframe key={`${shown}-${run}`} ref={frame} className="cs-player-frame" title={`${title}, by ${byline}`} sandbox={PREVIEW_SANDBOX} srcDoc={html} style={fit.frameStyle} />
        <BigExit fit={fit} />
      </div>
    </div>
  );
}
